import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ReactFlowProvider, useNodesState, useEdgesState, addEdge, type NodeChange, type EdgeChange } from '@xyflow/react';
import { Play, ArrowLeft, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useWorkflows } from './useWorkflows';
import { useOrgId } from '../../lib/data';
import { nodeDataForKind } from './nodeTypes';
import { FlowCanvas } from './FlowCanvas';
import { NodePalette } from './NodePalette';
import { NodeProperties } from './NodeProperties';
import { LogsPanel } from './LogsPanel';
import type { WorkflowNode, WorkflowNodeData, WorkflowNodeKind, WorkflowEdge } from './types';

interface WorkflowEditorProps {
  workflowId: string | null;
  onBack: () => void;
}

/**
 * Full-bleed workflow editor. Fills the viewport below the console header:
 *   ┌ header (back · name · run) ──────────────────────────┐
 *   │ ┌ palette ┐ ┌ canvas (fills) ──────────────────────┐  │
 *   │ │ groups  │ │  ReactFlow                  [config]│  │
 *   │ │         │ │                              floats │  │
 *   │ │         │ │  [logs]                             │  │
 *   │ └─────────┘ └─────────────────────────────────────┘  │
 *   └──────────────────────────────────────────────────────┘
 *
 * Node config floats top-right and shows only while a node is selected
 * (click node → show; click empty canvas / panel X → hide). State management
 * is ported from the legacy WorkflowStudio: local RF nodes/edges are the
 * canvas source of truth, synced into the shared collection via updateGraph
 * (debounced autosave) and flushed on unmount.
 */
export function WorkflowEditor({ workflowId, onBack }: WorkflowEditorProps) {
  const { graphs, loading, error, saveError, updateGraph, flushSave } = useWorkflows();
  const orgId = useOrgId();

  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdge>([]);
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [logs, setLogs] = useState<string[]>([
    'Workflow editor ready. Drag a node from the palette, connect handles, edit params.',
  ]);
  const [running, setRunning] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(true);

  const graph = useMemo(() => graphs.find((g) => g.id === workflowId) ?? null, [graphs, workflowId]);

  const initRef = useRef<string | null>(null);   // graph id we've seeded into RF state
  const pendingSyncRef = useRef(false);          // a user edit is awaiting sync → collection
  const idCounter = useRef(0);
  const runTimersRef = useRef<number[]>([]);
  const flushRef = useRef(flushSave);
  useEffect(() => { flushRef.current = flushSave; }, [flushSave]);

  const appendLog = useCallback((line: string) => {
    setLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${line}`]);
  }, []);

  // Surface autosave failures (parity with the legacy studio) so a rejected PUT
  // (permissions, network) isn't silently swallowed.
  useEffect(() => {
    if (saveError) appendLog(`⚠ ${saveError}`);
  }, [saveError, appendLog]);

  // ---- seed RF state once the graph arrives (and when the id changes) ----
  // initRef guards re-seed on graph.id, but we reset it on org change so a
  // different org's graph that happens to share the same id still re-seeds
  // (otherwise the editor would keep the previous org's stale nodes/edges and
  // a subsequent edit would corrupt the new org's graph).
  useEffect(() => { initRef.current = null; }, [orgId]);
  useEffect(() => {
    if (!graph || initRef.current === graph.id) return;
    initRef.current = graph.id;
    setNodes(graph.nodes);
    setEdges(graph.edges);
  }, [graph, setNodes, setEdges]);

  // ---- sync RF changes back into the shared collection (→ autosave) ----
  // Depends on nodes/edges ONLY (not `graph`): updateGraph re-creates the graph
  // object, so depending on it would loop. Gated on pendingSyncRef, which ONLY
  // user-action handlers set — so the seed (setNodes/setEdges directly) never
  // syncs. This avoids both an empty-write on mount (sync effect seeing nodes=[]
  // before the seed re-renders) and a wasted "seed-echo" save on every open.
  useEffect(() => {
    const id = initRef.current;
    if (!id || !pendingSyncRef.current) return;
    pendingSyncRef.current = false;
    updateGraph(id, { nodes, edges });
  }, [nodes, edges, updateGraph]);

  // Flush pending autosave + cancel run timers on unmount.
  // ponytail: flushSave's PUT races the WorkflowList's mount-time GET — if the
  // GET lands at D1 first, the list briefly shows pre-edit graphs (data is
  // persisted, not lost; corrected on the next navigation). Acceptable for an
  // editor; upgrade to a module-level graphs singleton the list hydrates from
  // before refetching if this ever surfaces.
  useEffect(() => () => {
    runTimersRef.current.forEach((t) => clearTimeout(t));
    flushRef.current();
  }, []);

  const handleNodesChange = useCallback((changes: NodeChange<WorkflowNode>[]) => {
    onNodesChange(changes);
    // Only data-bearing changes should sync+save — selection / dimension
    // changes are transient UI state and must NOT persist to D1.
    if (changes.some((c) => c.type === 'position' || c.type === 'remove')) pendingSyncRef.current = true;
  }, [onNodesChange]);

  const handleEdgesChange = useCallback((changes: EdgeChange<WorkflowEdge>[]) => {
    onEdgesChange(changes);
    if (changes.some((c) => c.type === 'remove' || c.type === 'add')) pendingSyncRef.current = true;
  }, [onEdgesChange]);

  const handleConnect = useCallback((connection: Parameters<typeof addEdge>[0]) => {
    setEdges((eds) => addEdge({ ...connection, animated: false }, eds));
    pendingSyncRef.current = true;
  }, [setEdges]);

  const handleNodeClick = useCallback((_e: unknown, node: { id: string }) => {
    setSelectedNodeId((prev) => (prev === node.id ? '' : node.id));
  }, []);

  const handlePaneClick = useCallback(() => setSelectedNodeId(''), []);

  const addNode = useCallback((kind: WorkflowNodeKind, position?: { x: number; y: number }) => {
    idCounter.current += 1;
    const id = `node-${kind}-${Date.now()}-${idCounter.current}`;
    const node: WorkflowNode = {
      id,
      type: 'workflow',
      position: position ?? { x: 240 + Math.random() * 120, y: 120 + Math.random() * 120 },
      data: nodeDataForKind(kind),
    };
    setNodes((nds) => nds.concat(node));
    setSelectedNodeId(id);
    pendingSyncRef.current = true;
    appendLog(`[SYSTEM] Added ${kind} node.`);
  }, [setNodes, appendLog]);

  const updateNodeData = useCallback((nodeId: string, patch: Partial<WorkflowNodeData>) => {
    setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)));
    pendingSyncRef.current = true;
  }, [setNodes]);

  const updateParam = useCallback((nodeId: string, key: string, value: string | number | boolean) => {
    setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, params: { ...n.data.params, [key]: value } } } : n)));
    pendingSyncRef.current = true;
  }, [setNodes]);

  const deleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNodeId('');
    pendingSyncRef.current = true;
    appendLog(`[SYSTEM] Removed node ${nodeId}.`);
  }, [setNodes, setEdges, appendLog]);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  const runFlow = useCallback(() => {
    if (running || nodes.length === 0) return;
    // No workflow runtime is wired yet. The graph editor persists the flow
    // definition (GET/PUT) but nothing executes it — so we must not fabricate
    // a "run complete". Show the honest unsupported state instead.
    setRunning(true);
    runTimersRef.current.forEach((t) => clearTimeout(t));
    setEdges((eds) => eds.map((e) => ({ ...e, animated: false })));
    appendLog(`⚠️ "${graph?.name ?? workflowId}" is saved as a definition, but no workflow executor is connected.`);
    appendLog('No run was started — nothing was executed or changed.');
    runTimersRef.current.push(window.setTimeout(() => setRunning(false), 1200));
  }, [running, nodes, workflowId, graph, appendLog, setEdges]);

  // ---- render guards ----
  if (loading) {
    return <div className="p-8 text-sm" style={{ color: 'var(--ink-secondary)' }}>Loading workflow…</div>;
  }
  if (error) {
    return (
      <div className="p-8 space-y-3">
        <div className="text-sm" style={{ color: 'var(--danger, #D32F2F)' }}>{error}</div>
        <button onClick={onBack} className="text-xs px-3 py-1.5 rounded border" style={{ borderColor: 'var(--border)', color: 'var(--ink)' }}>← Back to workflows</button>
      </div>
    );
  }
  if (!graph) {
    return (
      <div className="p-8 space-y-3">
        <div className="text-sm" style={{ color: 'var(--ink-secondary)' }}>Workflow not found. It may have been deleted.</div>
        <button onClick={onBack} className="text-xs px-3 py-1.5 rounded border" style={{ borderColor: 'var(--border)', color: 'var(--ink)' }}>← Back to workflows</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100dvh - 3.5rem)' }} id="workflow-editor-root">

      {/* Editor header */}
      <header className="h-12 shrink-0 border-b flex items-center gap-3 px-3" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs px-2 py-1.5 rounded transition"
          style={{ color: 'var(--ink-secondary)' }}
          title="Back to workflows"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Workflows</span>
        </button>
        <button
          onClick={() => setPaletteOpen((v) => !v)}
          className="p-1.5 rounded transition"
          style={{ color: 'var(--ink-secondary)' }}
          title={paletteOpen ? 'Hide palette' : 'Show palette'}
          aria-label="Toggle palette"
        >
          {paletteOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
        </button>
        <div className="min-w-0">
          <h2 className="text-sm font-semibold truncate" style={{ color: 'var(--ink)' }}>{graph.name}</h2>
          <p className="text-[10px] truncate" style={{ color: 'var(--ink-secondary)' }}>
            {nodes.length} nodes · {edges.length} connections
          </p>
        </div>
        <button
          onClick={runFlow}
          disabled={running || nodes.length === 0}
          className="ml-auto flex items-center gap-1.5 text-white text-xs font-semibold px-3.5 py-2 rounded-lg shadow-sm transition disabled:opacity-50"
          style={{ background: 'var(--accent)' }}
        >
          <Play className={`w-3.5 h-3.5 fill-current ${running ? 'animate-pulse' : ''}`} />
          {running ? 'Running…' : 'Run Flow'}
        </button>
      </header>

      {/* Body: palette | canvas */}
      <div className="relative flex flex-1 min-h-0">
        <aside
          className={`${paletteOpen ? 'flex' : 'hidden'} md:flex absolute inset-y-0 left-0 z-30 md:relative md:inset-auto flex-col w-64 max-w-[calc(100%-1rem)] shrink-0 border-r overflow-y-auto`}
          style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
          id="workflow-palette-sidebar"
        >
          <NodePalette onAdd={(k) => addNode(k)} />

          <div className="m-4 mt-0 rounded-lg border p-3 text-xs space-y-1.5 font-mono" style={{ borderColor: 'var(--border)', background: 'var(--ink-faint)', color: 'var(--ink-secondary)' }}>
            <div className="flex items-center justify-between">
              <span>Active nodes:</span>
              <span className="font-bold" style={{ color: 'var(--accent)' }}>{nodes.length}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Connections:</span>
              <span className="font-bold" style={{ color: 'var(--accent)' }}>{edges.length}</span>
            </div>
          </div>
        </aside>

        <div className="relative flex-1 min-w-0 flex flex-col" id="workflow-canvas-region">
          <div className="relative flex-1 min-h-0">
            <ReactFlowProvider>
              <FlowCanvas
                key={graph.id}
                nodes={nodes}
                edges={edges}
                onNodesChange={handleNodesChange}
                onEdgesChange={handleEdgesChange}
                onConnect={handleConnect}
                onNodeClick={handleNodeClick}
                onPaneClick={handlePaneClick}
                onDropKind={(kind, pos) => addNode(kind, pos)}
              />
            </ReactFlowProvider>

            {/* Floating node config — shows only while a node is selected */}
            <NodeProperties
              node={selectedNode}
              onUpdateData={updateNodeData}
              onUpdateParam={updateParam}
              onDelete={deleteNode}
              onClose={() => setSelectedNodeId('')}
            />
          </div>

          <LogsPanel logs={logs} onClear={() => setLogs([])} />
        </div>
      </div>
    </div>
  );
}
